import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, Shuffle } from "lucide-react";
import { toast } from "sonner";

export default function ReassignBinDialog({ load, stockpiles, onClose, onSaved }) {
  const [newBin, setNewBin] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const tons = parseFloat(load.posted_tons ?? load.weight_tons) || 0;
  const selected = stockpiles.find((sp) => sp.id === newBin);
  const cap = selected ? parseFloat(selected.capacity_tons) || 0 : 0;
  const cur = selected ? parseFloat(selected.current_tons) || 0 : 0;
  const wouldOverflow = selected && cap > 0 && (cur + tons) > cap;
  const overBy = wouldOverflow ? (cur + tons - cap).toFixed(1) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!newBin) { toast.error("Choose a new bin"); return; }
    if (!reason.trim()) { toast.error("Reason is required for a reassignment"); return; }
    if (wouldOverflow) {
      toast.error(`Bin ${selected.bin_number} would overflow by ${overBy}t.`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/loads/${load.id}/reassign-bin`, {
        new_stockpile_id: newBin,
        reason: reason.trim(),
      });
      toast.success("Bin reassigned");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-md">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="label-caps flex items-center gap-1.5 text-orange-700"><Shuffle className="w-3 h-3" /> Compliance</div>
            <div className="font-display font-bold text-xl">Reassign Bin</div>
            <div className="text-xs text-gray-500 mt-0.5 font-mono">
              {load.bol_number} · currently posted to bin <span className="font-bold">{load.bin_number || "—"}</span> · {(load.posted_tons ?? load.weight_tons)}t
            </div>
          </div>
          <button onClick={onClose} data-testid="close-reassign-bin"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <F label="New Bin">
            <select
              required
              value={newBin}
              onChange={(e) => setNewBin(e.target.value)}
              className="input-sharp"
              data-testid="reassign-bin-select"
            >
              <option value="">Select a different bin at the same site…</option>
              {stockpiles.map((sp) => (
                <option key={sp.id} value={sp.id}>
                  {sp.bin_number} · {sp.name} · {sp.classification} · {(sp.current_tons ?? 0).toFixed(1)}/{sp.capacity_tons}t
                </option>
              ))}
            </select>
            {stockpiles.length === 0 && (
              <div className="text-[11px] text-red-600 mt-1">No other bins at this drop-off site. Create one under Stockpiles first.</div>
            )}
            {selected && cap > 0 && (
              <div className="text-[11px] text-gray-500 mt-1 font-mono">
                Bin capacity: {cur.toFixed(1)} / {cap}t{" · "}
                <span className={wouldOverflow ? "text-red-600 font-bold" : "text-emerald-700"}>
                  {wouldOverflow ? `over by ${overBy}t` : `room for ${(cap - cur).toFixed(1)}t`}
                </span>
              </div>
            )}
          </F>
          <F label="Reason (required, audit-logged)">
            <textarea
              required
              rows="3"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="input-sharp"
              data-testid="reassign-reason-input"
              placeholder="e.g. Lab reclassified sample as regulated; moved from clean bin B04 to regulated bin R02."
            />
          </F>
          <div className="text-[11px] text-orange-800 bg-orange-50 border border-orange-200 p-2">
            Submitting will move <span className="font-mono font-bold">{load.posted_tons ?? load.weight_tons}t</span> off bin <span className="font-mono font-bold">{load.bin_number}</span> (adds an OUT reversal movement) and post it to the new bin (IN movement). A custody event <span className="font-mono">bin_reassigned</span> is appended to this load.
          </div>
          {wouldOverflow && (
            <div className="text-[11px] text-red-800 bg-red-50 border border-red-200 p-2" data-testid="reassign-capacity-warning">
              Bin <span className="font-mono font-bold">{selected.bin_number}</span> cannot hold this load — over by <span className="font-mono font-bold">{overBy}t</span>. Choose a different bin.
            </div>
          )}
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button type="submit" disabled={busy || stockpiles.length === 0 || wouldOverflow} data-testid="submit-reassign-bin" className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2.5 text-xs uppercase tracking-widest font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? "Reassigning…" : "Reassign Bin"}
            </button>
          </div>
        </form>
        <style>{`.input-sharp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; background: white; } .input-sharp:focus { outline: none; border-color: #111827; box-shadow: 0 0 0 1px #111827; }`}</style>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="label-caps block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
